import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Phone, Loader2 } from 'lucide-react';
import { PromotionPageLayout } from '@/components/layout/PromotionPageLayout';
import { PromotionModalHeader } from '@/components/layout/PromotionModalHeader';
import { PromotionModalFooter } from '@/components/layout/PromotionModalFooter';
import { ContactFields } from './components/ContactFields';
import { PrivacyPromise } from './components/PrivacyPromise';
import { ROUTES } from '@/constants/routes';
import { isEmailValid, isPhoneValid } from '@/lib/validators';
import promotionService from '@/services/promotionService';

export default function PromotionContact() {
  const navigate = useNavigate();
  const location = useLocation();
  const incoming = location.state ?? {};
  const isUpdate = incoming.isUpdate === true;

  useEffect(() => {
    if (!incoming.role) {
      navigate(ROUTES.promotionEnrollment, { replace: true });
    } else if (!incoming.days || incoming.days.length === 0) {
      navigate(ROUTES.promotionAvailability, {
        replace: true,
        state: incoming,
      });
    }
  }, [incoming, navigate]);

  const isParent = incoming.role === 'parent';
  const audience = isParent ? "Parent's" : "Student's";

  const [values, setValues] = useState({
    name: incoming.contact?.name ?? '',
    phone: incoming.contact?.phone ?? '',
    email: incoming.contact?.email ?? '',
  });
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submissionId] = useState(() => incoming.submissionId ?? crypto.randomUUID());

  const nameOk = values.name.trim().length > 0;
  const phoneOk = isPhoneValid(values.phone);
  const emailOk = isEmailValid(values.email);
  const formOk = nameOk && phoneOk && emailOk;

  const errors = {
    name: touched.name && !nameOk ? 'Please enter a name' : null,
    phone:
      touched.phone && !phoneOk ? 'Please enter a valid phone number' : null,
    email:
      touched.email && !emailOk ? 'Please enter a valid email address' : null,
  };

  function handleChange(field, value) {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (submitError) setSubmitError(null);
  }

  function handleBlur(field) {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setTouched({ name: true, phone: true, email: true });
    if (!formOk || isSubmitting) return;

    const trimmedContact = {
      name: values.name.trim(),
      phone: values.phone.trim(),
      email: values.email.trim(),
    };
    const studentForPayload = incoming.student
      ? { ...incoming.student, name: trimmedContact.name }
      : null;
    const payload = {
      role: incoming.role,
      student: studentForPayload,
      kids: incoming.kids,
      days: incoming.days,
      timeSlots: incoming.timeSlots,
      contact: trimmedContact,
      region: incoming.region ?? 'dfw',
      submissionId,
      isUpdate,
    };

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const response = await promotionService.enroll(payload);
      if (response.ok) {
        navigate(ROUTES.promotionConfirmation, {
          state: { ...incoming, contact: trimmedContact },
        });
        return;
      }
      const body = await response.json().catch(() => null);
      const firstError = body?.validationErrors?.[0];
      setSubmitError(
        firstError ?? 'Something went wrong. Please check your info and try again.'
      );
    } catch (err) {
      if (err.name === 'AbortError') {
        setSubmitError('The request took too long. Please try again.');
      } else {
        setSubmitError('Could not reach the server. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const buttonLabel = isSubmitting
    ? (isUpdate ? 'Updating...' : 'Enrolling...')
    : (isUpdate ? 'Save Changes' : 'Enroll Now');
  const overlayLabel = isUpdate
    ? 'Updating your enrollment...'
    : 'Submitting your enrollment...';

  return (
    <>
      {isSubmitting && (
        <div
          role="status"
          aria-live="polite"
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm"
        >
          <div className="rounded-2xl bg-white px-8 py-6 shadow-2xl">
            <div className="flex items-center gap-3">
              <Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" />
              <span className="text-base font-medium text-slate-700">{overlayLabel}</span>
            </div>
          </div>
        </div>
      )}
      <PromotionPageLayout step={4} totalSteps={4} modal modalSize="md">
        <PromotionModalHeader
          icon={Phone}
          title="Almost done."
          subtitle={`Just need the ${isParent ? "parent's" : "student's"} contact info so we can confirm the spot.`}
          backTo={ROUTES.promotionAvailability}
          backState={incoming}
        />
        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-5 px-5 py-6 sm:px-8 sm:py-7">
            <ContactFields
              audience={audience}
              values={values}
              errors={errors}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {submitError && (
              <div
                role="alert"
                className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                {submitError}
              </div>
            )}
            <PrivacyPromise />
          </div>
          <PromotionModalFooter disabled={!formOk || isSubmitting}>
            {buttonLabel}
          </PromotionModalFooter>
        </form>
      </PromotionPageLayout>
    </>
  );
}